import { FC, ReactNode } from "react";

import { I18nKeyType } from "@libs/i18n";
import { RmLabel } from "@/components/RmLabel";
import { RmSkeleton } from "@/components/RmSkeleton";
import { RmText } from "@/components/RmText/RmText";
import { useTranslate } from "@/hooks/useTranslate";
import { cn } from "@/utils/tailwind";

type LabelAndValuePropsType = {
  labelI18nKey: I18nKeyType;
  labelI18nOptions?: object;
  labelClassName?: string;
  wrapperClassName?: string;
  value?: string;
  valueI18nKey?: I18nKeyType;
  valueI18nOptions?: object;
  valueClassName?: string;
  children?: ReactNode;
  isLoading?: boolean;
  isHidden?: boolean;
  skeletonClassName?: string;
  errorMessageI18nKey?: I18nKeyType;
};

const LabelAndValue: FC<LabelAndValuePropsType> = ({
  labelI18nKey,
  labelI18nOptions,
  labelClassName,
  value,
  valueI18nKey,
  valueI18nOptions,
  valueClassName,
  children,
  wrapperClassName,
  isLoading = false,
  isHidden = false,
  skeletonClassName,
  errorMessageI18nKey,
}) => {
  const valueContent: string | undefined =
    useTranslate(valueI18nKey, valueI18nOptions) ?? value;
  const errorMessage = useTranslate(errorMessageI18nKey);

  if (isHidden) return null;

  return (
    <div className={cn("flex flex-col gap-0.5", wrapperClassName)}>
      <RmLabel
        i18nKey={labelI18nKey}
        i18nOptions={labelI18nOptions}
        className={cn("text-muted-foreground", labelClassName)}
      />
      <div
        className={cn("px-0.5 py-1", {
          "py-1.5": isLoading,
        })}>
        {isLoading ? (
          <RmSkeleton className={cn("h-4 max-w-50", skeletonClassName)} />
        ) : (
          (children ?? (
            <RmText
              className={cn(valueClassName, {
                "text-destructive": !valueContent && errorMessage,
              })}>
              {valueContent ?? errorMessage}
            </RmText>
          ))
        )}
      </div>
    </div>
  );
};

export { LabelAndValue };
